import { ReactNode, useState } from 'react'
import cn from 'classnames'
import { useAppStore } from '@forwardflow/store/appStore'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { useAccountReadiness } from '../../hooks/useAccountReadiness'
import { IReadiness, ITrackerStep } from '../../utils/readiness'
import { Button, Spinner } from '../ui/primitives'
import { Icon } from '../ui/Icon'
import '../onboarding-v2.css'

const DOT: Record<ITrackerStep['state'], string> = {
   done: 'bg-[var(--ob-good-tint)] text-[var(--ob-good)]',
   working: 'bg-[var(--ob-primary-tint)] text-[var(--ob-light)]',
   waiting: 'bg-[var(--ob-surface-2)] text-[var(--ob-text-3)]',
   blocked: 'bg-[var(--ob-warn-tint)] text-[var(--ob-warn)]',
   info: 'bg-[var(--ob-surface-2)] text-[var(--ob-text-2)]',
}

const StepRow = ({ step }: { step: ITrackerStep }) => (
   <li
      className="flex items-start gap-4 px-5 py-4"
      data-testid={`setup-step-${step.id}`}
      data-state={step.state}
   >
      <span
         className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
            DOT[step.state]
         )}
      >
         {step.state === 'done' ? (
            <Icon name="check" size={16} />
         ) : step.state === 'working' ? (
            <Spinner size={16} />
         ) : step.state === 'blocked' ? (
            <Icon name="alert" size={16} />
         ) : step.id === 'texting' ? (
            <Icon name="message" size={16} />
         ) : (
            <Icon name="gauge" size={16} />
         )}
      </span>
      <div className="min-w-0">
         <p
            className={cn(
               'text-[15px] font-semibold',
               step.state === 'waiting' && 'text-[var(--ob-text-2)]'
            )}
         >
            {step.title}
         </p>
         <p className="text-[13px] text-[var(--ob-text-2)] mt-0.5">
            {step.detail}
         </p>
      </div>
   </li>
)

/**
 * "We're setting up your account": what the background is doing, step by
 * step, polled until the campaign flow can open. Nothing here waits on the
 * person; they can leave and come back.
 */
export const SetupTracker = ({
   readiness,
   onOpenAnyway,
   checking,
}: {
   readiness: IReadiness
   onOpenAnyway?: () => void
   checking?: boolean
}) => (
   <div
      className="ob relative min-h-[calc(100vh-120px)] rounded-[24px] flex items-center justify-center px-4 py-10"
      data-testid="setup-tracker"
   >
      <div className="w-full max-w-[560px]">
         <div className="text-center mb-7">
            <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ob-primary-tint)] text-[var(--ob-light)]">
               <Spinner size={24} />
            </span>
            <h1 className="text-[26px] md:text-[30px] font-semibold tracking-[-0.02em]">
               We&rsquo;re setting up your account
            </h1>
            <p className="text-[15px] text-[var(--ob-text-2)] mt-2 max-w-[460px] mx-auto">
               Your first campaign opens here as soon as your leads are in and
               calling is set up. You can close this page; your setup keeps
               going.
            </p>
         </div>
         <ol className="rounded-[20px] border border-[var(--ob-border)] bg-[var(--ob-surface)] divide-y divide-[var(--ob-border)] overflow-hidden">
            {readiness.steps.map((s) => (
               <StepRow key={s.id} step={s} />
            ))}
         </ol>
         <div className="mt-5 flex items-center justify-center gap-2 text-[13px] text-[var(--ob-text-3)]">
            {checking ? <Spinner size={12} /> : <Icon name="check" size={12} />}
            <span>Checks again on its own every 20 seconds.</span>
         </div>
         {onOpenAnyway && (
            <div className="mt-6 flex justify-center">
               <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOpenAnyway}
                  testId="setup-open-anyway"
               >
                  Open the campaign builder anyway
               </Button>
            </div>
         )}
      </div>
   </div>
)

/**
 * Holds the campaign flow behind the tracker while a new account is still
 * being set up. An organization that is RUNNING, or whose leads and campaign
 * engine are ready, goes straight through; so does anyone if the checks
 * themselves fail (unknown never blocks).
 */
/**
 * "Open anyway" is a decision for the whole session, not one page view:
 * every gated route mounts its own gate, so the choice is kept in
 * sessionStorage per organization and read back on every mount.
 */
const overrideKey = (orgId: string) => `ff-campaign-gate-override:${orgId}`
const readOverride = (orgId: string) => {
   try {
      return sessionStorage.getItem(overrideKey(orgId)) === '1'
   } catch {
      return false
   }
}
const writeOverride = (orgId: string) => {
   try {
      sessionStorage.setItem(overrideKey(orgId), '1')
   } catch {
      /* Storage blocked: the in-memory state still covers this mount. */
   }
}

export const CampaignReadinessGate = ({
   children,
}: {
   children: ReactNode
}) => {
   const orgId = useAppStore((s) => s.currentOrganization)
   const { data: me } = useAuthUser({})
   const { readiness, isLoading } = useAccountReadiness(orgId)
   const [overrideNow, setOverrideNow] = useState(false)
   const override = overrideNow || (!!orgId && readOverride(orgId))

   if (!orgId || override) return <>{children}</>
   if (!readiness)
      return isLoading ? (
         <div
            className="flex min-h-[50vh] items-center justify-center text-[var(--ob-text-2,#9ca3af)]"
            data-testid="setup-checking"
         >
            <Spinner size={22} />
         </div>
      ) : (
         <>{children}</>
      )
   if (readiness.canBuild) return <>{children}</>

   // Staff helping a customer, or leads that never show because the account
   // simply has none: the engine being ready is enough to let them in.
   const operator = isOperatorEmail(me?.email || '')
   const canOverride = operator || readiness.engineReady === true
   return (
      <SetupTracker
         readiness={readiness}
         checking={isLoading}
         onOpenAnyway={
            canOverride
               ? () => {
                    writeOverride(orgId)
                    setOverrideNow(true)
                 }
               : undefined
         }
      />
   )
}
